import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, verify as apiVerify } from '../api/auth';
import { showError, showSuccess } from '../utils/notifications';

// User interface
interface User {
  id: number;
  username: string;
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props interface
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Changed to true initially
  const navigate = useNavigate();

  // Function to check if the user is authenticated with the server
  const checkAuth = async (): Promise<boolean> => {
    try {
      const response = await apiVerify();
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      return true;
    } catch (error) {
      setUser(null);
      localStorage.removeItem('user');
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check authentication status on mount
  useEffect(() => {
    const verifyAuth = async () => {
      // First try from localStorage for quick UI rendering
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Failed to parse stored user:', error);
          localStorage.removeItem('user');
        }
      }
      
      // Then verify with the server
      await checkAuth();
    };
    
    verifyAuth();
  }, []);

  // Login function
  const login = async (username: string, password: string) => {
    setIsLoading(true);
    
    try {
      const response = await apiLogin({ username, password });
      
      // Store user in state and localStorage
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      showSuccess('Login successful');
      navigate('/');
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      showError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Auth context value
  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth; 